'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'

import { VideoGrant } from 'livekit-server-sdk'
import { clientLogError } from '@/src/lib/clientLog'

import { LocalUserChoices } from '@/src'
import { ActiveRoom } from '@/src/prefabs/ActiveRoom'
import { FedorBusyScreen } from '@/src/components/FedorBusyScreen'
import {getInterviewAdmissionFailure, shouldCheckInterviewLimit, type InterviewAdmissionFailure} from '@/src/lib/interviewAdmission'
import {clearAccessToken} from '@/src/store/slices/token'

import { preJoinDefaults } from '@/src/constants/preJoin'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin, selectRoomName, selectSettings } from '@/src/store/slices/meetData'
import {
  selectPhone,
  selectProject,
  selectRoomType,
  selectReferrer,
  selectRoomStatus,
  setStatus,
} from '@/src/store/slices/room'

import {
  shouldAutoRejoin,
  sessionToUserChoices,
  clearMeetingSession,
  consumeForcePrejoinFlag,
} from '@/src/hooks/room/useMeetingSession'
import { useIsMobile } from '@/src/hooks/useIsMobile'
import { recordInterviewConsentJournal } from '@/src/lib/interviewConsents'
import { RoomLookupErrorKind } from '@/src/lib/roomLookupError'
import { canJoinAfterRoomLookup } from '@/src/lib/roomLookupJoin'
import { RoomLookupErrorOverlay } from '@/src/components/RoomLookupErrorOverlay'

/**
 * Ошибка lookup комнаты со страницы, чтобы не входить в звонок без данных.
 */
interface HomeOfCustomDomainProps {
  roomLookupError?: RoomLookupErrorKind | null
}

const PreJoin = dynamic(
  async () => (await import('@/src/prefabs/PreJoin/PreJoinSettings')).JoinSetting,
  { ssr: false, loading: () => null },
)

const HomeOfCustomDomain = ({ roomLookupError = null }: HomeOfCustomDomainProps) => {
  const dispatch = useAppDispatch()
  const roomName = useAppSelector(selectRoomName)
  const status = useAppSelector(selectRoomStatus)
  const phone = useAppSelector(selectPhone)
  const project = useAppSelector(selectProject)
  const roomType = useAppSelector(selectRoomType)
  const referrer = useAppSelector(selectReferrer)
  const branding = useAppSelector(selectBranding)
  const settings = useAppSelector(selectSettings)
  const isAdmin = useAppSelector(selectIsAdmin)
  const { query } = useRouter()
  const { isMobileByUserAgent, isInAppBrowser } = useIsMobile()
  const canJoin = canJoinAfterRoomLookup(roomLookupError)

  const [statusWaitingRoom, setStatusWaitingRoom] = useState<boolean>(false)
  const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices>(null)
  const [admissionFailure, setAdmissionFailure] = useState<InterviewAdmissionFailure | null>(null)
  const [isAutoRejoining, setIsAutoRejoining] = useState<boolean>(false)

  // Авто-вход при перезагрузке страницы (только на мобильных)
  useEffect(() => {
    // Одноразовый флаг после «Войти ещё раз» с экрана ошибки: человек должен
    // увидеть форму входа, а не молча вернуться в тот же звонок.
    if (!canJoinAfterRoomLookup(roomLookupError)) return

    if (admissionFailure || consumeForcePrejoinFlag()) return

    // Активируем только на мобильных и in-app браузерах
    if (!isMobileByUserAgent && !isInAppBrowser) return
    if (!roomName || Array.isArray(roomName)) return
    if (preJoinChoices) return // Уже есть выбор пользователя

    const savedSession = shouldAutoRejoin(roomName)

    if (savedSession) {
      console.log('[AutoRejoin] Found saved session, auto-rejoining meeting')
      setIsAutoRejoining(true)

      // Конвертируем сессию в userChoices и автоматически входим
      const choices = sessionToUserChoices(savedSession)
      setPreJoinChoices(choices)
      dispatch(setStatus('loading'))

      // Очищаем сессию после использования
      clearMeetingSession()

      setIsAutoRejoining(false)
    }
  }, [roomName, isMobileByUserAgent, isInAppBrowser, preJoinChoices, dispatch, roomLookupError, admissionFailure])

  const isHidden = useMemo(() => query.hidden === 'true', [query.hidden])

  const handleAdmissionDenied = useCallback((reason: InterviewAdmissionFailure) => {
    setAdmissionFailure(reason)
    setPreJoinChoices(null)
    dispatch(clearAccessToken())
    dispatch(setStatus('prejoin'))
  }, [dispatch])

  const handlePreJoinSubmit = useCallback(
    async (values: LocalUserChoices) => {
      if (!canJoinAfterRoomLookup(roomLookupError)) {
        return
      }

      let prejoinInterviewId: string | null = null
      const admissionContext = {roomType, project, v: query.v, interview: query.interview}
      if (!isAdmin && roomName && !Array.isArray(roomName) && shouldCheckInterviewLimit(admissionContext)) {
        try {
          const response = await fetch('/api/prejoin_check', {
            method: 'POST', headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({...admissionContext, roomName, candidateName: values.username,
              phone: String((values as any).phone || phone || ''), referrer}),
          })
          const data = await response.json()
          prejoinInterviewId = typeof data?.interviewId === 'string' ? data.interviewId : null
          const failure = getInterviewAdmissionFailure(response.ok ? data : {canJoin: false, reason: 'check_unavailable'})
          if (failure) {
            handleAdmissionDenied(failure)
            return
          }
        } catch {
          handleAdmissionDenied('check_unavailable')
          return
        }
      }

      if (roomName && !Array.isArray(roomName)) {
        await recordInterviewConsentJournal({
          roomName,
          values,
          phone,
          referrer,
          project,
          interviewId: prejoinInterviewId,
        })
      }

      setAdmissionFailure(null)
      setPreJoinChoices(values)
      dispatch(setStatus('loading'))
    },
    [dispatch, phone, project, roomType, referrer, roomName, roomLookupError, isAdmin,
      query.v, query.interview, handleAdmissionDenied],
  )

  const handlePreJoinStatus = useCallback((values) => {
    setStatusWaitingRoom(values)
  }, [])

  const handleOnError = useCallback((err: Error) => {
    console.error('error while setting up prejoin', err)
    clientLogError(`error while setting up prejoin: ${err?.message}`)
  }, [])

  const videoGrant = useMemo<VideoGrant>(
    () => ({
      canSubscribe: settings.wait_room ? Boolean(statusWaitingRoom || isAdmin) : true,
      canPublish: isHidden ? false : settings.wait_room ? Boolean(statusWaitingRoom || isAdmin) : true,
      // roomAdmin в JWT должен совпадать с кнопкой «впустить» (Redux isAdmin),
      // а не только с флагом «уже прошёл зал ожидания».
      roomAdmin: Boolean(statusWaitingRoom || isAdmin),
      hidden: isHidden,
    }),
    [settings.wait_room, statusWaitingRoom, isHidden, isAdmin],
  )

  return (
    <main data-lk-theme="default">
      {admissionFailure ? (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
          <FedorBusyScreen reason={admissionFailure} onBack={() => setAdmissionFailure(null)} />
        </div>
      ) : canJoin && roomName && !Array.isArray(roomName) && preJoinChoices && status !== 'prejoin' ? (
        <ActiveRoom roomName={roomName} videoGrant={videoGrant} userChoices={preJoinChoices}
          onAdmissionDenied={handleAdmissionDenied} />
      ) : (
        <div style={{ display: 'grid', placeItems: 'center', height: '100%', overflowY: 'auto' }}>
          <PreJoin
            defaults={preJoinDefaults}
            parsBrandingData={branding}
            statusWaitingRoom={handlePreJoinStatus}
            onSubmit={handlePreJoinSubmit}
            onError={handleOnError}
          />
          <RoomLookupErrorOverlay kind={roomLookupError} />
        </div>
      )}
    </main>
  )
}

export default HomeOfCustomDomain
